
import React, { PureComponent } from 'react';

import { getWindowWidth } from '../../../environment/utils';

type Props = {

    /**
     * The child component to make draggable and resizable.
     */
    children: React$Node,

    /**
     * Initial position of the component (from bottom-right corner).
     */
    initialPosition?: {
        x: number,
        y: number
    },

    /**
     * Initial size of the component.
     */
    initialSize?: {
        width: number,
        height: number
    },

    /**
     * Minimum size constraints.
     */
    minSize?: {
        width: number,
        height: number
    },

    /**
     * Maximum size constraints.
     */
    maxSize?: {
        width: number,
        height: number
    },

    /**
     * Whether the component should be draggable.
     */
    draggable?: boolean,

    /**
     * Whether the component should be resizable.
     */
    resizable?: boolean,

    /**
     * CSS class name for the container.
     */
    className?: string,

    /**
     * Callback when drag starts.
     */
    onDragStart?: () => void,

    /**
     * Callback when dragging.
     */
    onDrag?: (position: { x: number, y: number }) => void,

    /**
     * Callback when drag ends.
     */
    onDragEnd?: (position: { x: number, y: number }) => void,

    /**
     * Callback when resize starts.
     */
    onResizeStart?: () => void,

    /**
     * Callback when resizing.
     */
    onResize?: (size: { width: number, height: number }) => void,

    /**
     * Callback when resize ends.
     */
    onResizeEnd?: (size: { width: number, height: number }) => void
};

type State = {
    position: {
        x: number,
        y: number
    },
    size: {
        width: number,
        height: number
    },
    isDragging: boolean,
    isResizing: boolean,
    dragStart: {
        x: number,
        y: number
    },
    resizeStart: {
        x: number,
        y: number,
        width: number,
        height: number
    }
};

/**
 * A component that makes its children draggable and resizable.
 * Uses bottom-right positioning instead of top-left.
 */
export default class DraggableResizable extends PureComponent<Props, State> {
    static defaultProps = {
        draggable: true,

        // resizable: true,
        initialPosition: { x: 20,
            y: 20 }, // Distance from right and bottom edges
        initialSize: { width: 300,
            height: 170 },
        minSize: { width: 150,
            height: 80 },
        maxSize: { width: 600,
            height: 400 }
    };

    /**
     * Initializes a new {@code DraggableResizable} instance.
     *
     * @param {Props} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            position: props.initialPosition || { x: 90,
                y: 10 },
            size: props.initialSize || { width: 300,
                height: 170 },
            isDragging: false,
            isResizing: false,
            dragStart: { x: 0,
                y: 0 },
            resizeStart: { x: 0,
                y: 0,
                width: 0,
                height: 0 }
        };

        // Bind event handlers
        this._onMouseDown = this._onMouseDown.bind(this);
        this._onMouseMove = this._onMouseMove.bind(this);
        this._onMouseUp = this._onMouseUp.bind(this);
        this._onResizeMouseDown = this._onResizeMouseDown.bind(this);

        // Bind touch event handlers
        this._onTouchStart = this._onTouchStart.bind(this);
        this._onTouchMove = this._onTouchMove.bind(this);
        this._onTouchEnd = this._onTouchEnd.bind(this);
        this._onResizeTouchStart = this._onResizeTouchStart.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidMount()}.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        // Mouse events
        document.addEventListener('mousemove', this._onMouseMove);
        document.addEventListener('mouseup', this._onMouseUp);

        // Touch events
        document.addEventListener('touchmove', this._onTouchMove, { passive: false });
        document.addEventListener('touchend', this._onTouchEnd);
    }

    /**
     * Implements React's {@link Component#componentWillUnmount()}.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        // Remove mouse events
        document.removeEventListener('mousemove', this._onMouseMove);
        document.removeEventListener('mouseup', this._onMouseUp);

        // Remove touch events
        document.removeEventListener('touchmove', this._onTouchMove);
        document.removeEventListener('touchend', this._onTouchEnd);
    }

    /**
     * Gets pointer position from mouse or touch event.
     *
     * @param {MouseEvent|TouchEvent} e - The event.
     * @private
     * @returns {Object} The pointer position.
     */
    _getPointerPosition(e: MouseEvent | TouchEvent) {
        if (e.touches) {
            // Touch event
            return {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY
            };
        }

        // Mouse event
        return {
            x: e.clientX,
            y: e.clientY
        };
    }

    /**
     * Converts screen coordinates to bottom-right relative coordinates.
     *
     * @param {Object} screenPos - The screen position.
     * @private
     * @returns {Object} The bottom-right relative position.
     */
    _screenToBottomRight(screenPos: { x: number, y: number }) {
        return {
            x: getWindowWidth() - screenPos.x,
            y: window.innerHeight - screenPos.y
        };
    }

    /**
     * Converts bottom-right relative coordinates to screen coordinates.
     *
     * @param {Object} position - The bottom-right relative position.
     * @private
     * @returns {Object} The screen position.
     */
    _bottomRightToScreen(position: { x: number, y: number }) {
        return {
            x: getWindowWidth() - position.x,
            y: window.innerHeight - position.y
        };
    }

    /**
     * Handles mouse down event for dragging.
     *
     * @param {MouseEvent} e - The mouse event.
     * @private
     * @returns {void}
     */
    _onMouseDown(e: MouseEvent) {
        if (e.button !== 0 || !this.props.draggable) {
            return; // Only handle left click and if draggable
        }

        this._startDrag(e);
    }

    /**
     * Handles touch start event for dragging.
     *
     * @param {TouchEvent} e - The touch event.
     * @private
     * @returns {void}
     */
    _onTouchStart(e: TouchEvent) {
        if (!this.props.draggable || e.touches.length !== 1) {
            return; // Only handle single touch and if draggable
        }

        this._startDrag(e);
    }

    /**
     * Starts dragging operation.
     *
     * @param {MouseEvent|TouchEvent} e - The event.
     * @private
     * @returns {void}
     */
    _startDrag(e: MouseEvent | TouchEvent) {
        const { position } = this.state;
        const pointerPos = this._getPointerPosition(e);
        const screenPos = this._bottomRightToScreen(position);

        this.setState({
            isDragging: true,
            dragStart: {
                x: pointerPos.x - screenPos.x,
                y: pointerPos.y - screenPos.y
            }
        });

        this.props.onDragStart && this.props.onDragStart();
        e.preventDefault();
    }

    /**
     * Handles mouse down event for resizing.
     *
     * @param {MouseEvent} e - The mouse event.
     * @private
     * @returns {void}
     */
    _onResizeMouseDown(e: MouseEvent) {
        if (e.button !== 0) {
            return; // Only handle left click and if resizable
        }

        this._startResize(e);
    }

    /**
     * Handles touch start event for resizing.
     *
     * @param {TouchEvent} e - The touch event.
     * @private
     * @returns {void}
     */
    _onResizeTouchStart(e: TouchEvent) {
        if (e.touches.length !== 1) {
            return; // Only handle single touch and if resizable
        }

        this._startResize(e);
    }

    /**
     * Starts resizing operation.
     *
     * @param {MouseEvent|TouchEvent} e - The event.
     * @private
     * @returns {void}
     */
    _startResize(e: MouseEvent | TouchEvent) {
        const { size } = this.state;
        const pointerPos = this._getPointerPosition(e);

        this.setState({
            isResizing: true,
            resizeStart: {
                x: pointerPos.x,
                y: pointerPos.y,
                width: size.width,
                height: size.height
            }
        });

        this.props.onResizeStart && this.props.onResizeStart();
        e.preventDefault();
        e.stopPropagation();
    }

    /**
     * Handles mouse move event for dragging and resizing.
     *
     * @param {MouseEvent} e - The mouse event.
     * @private
     * @returns {void}
     */
    _onMouseMove(e: MouseEvent) {
        this._handlePointerMove(e);
    }

    /**
     * Handles touch move event for dragging and resizing.
     *
     * @param {TouchEvent} e - The touch event.
     * @private
     * @returns {void}
     */
    _onTouchMove(e: TouchEvent) {
        if (e.touches.length !== 1) {
            return; // Only handle single touch
        }

        this._handlePointerMove(e);
    }

    /**
     * Handles pointer move for both mouse and touch.
     *
     * @param {MouseEvent|TouchEvent} e - The event.
     * @private
     * @returns {void}
     */
    _handlePointerMove(e: MouseEvent | TouchEvent) {
        const { isDragging, isResizing, dragStart, resizeStart } = this.state;

        if (isDragging) {
            const pointerPos = this._getPointerPosition(e);

            // Calculate new screen position
            const newScreenPosition = {
                x: pointerPos.x - dragStart.x,
                y: pointerPos.y - dragStart.y
            };

            // Convert to bottom-right relative position
            const newPosition = this._screenToBottomRight(newScreenPosition);

            // Constrain to viewport
            const constrainedPosition = this._constrainToViewport(newPosition);

            this.setState({ position: constrainedPosition });
            this.props.onDrag && this.props.onDrag(constrainedPosition);
        } else if (isResizing) {
            const pointerPos = this._getPointerPosition(e);
            const deltaX = pointerPos.x - resizeStart.x;
            const deltaY = pointerPos.y - resizeStart.y;

            // For bottom-right positioning, positive deltaX means wider, positive deltaY means taller
            const newSize = {
                width: resizeStart.width + deltaX,
                height: resizeStart.height + deltaY
            };

            // Apply size constraints
            const constrainedSize = this._constrainSize(newSize);

            this.setState({ size: constrainedSize });
            this.props.onResize && this.props.onResize(constrainedSize);
        }
    }

    /**
     * Handles mouse up event to end dragging or resizing.
     *
     * @private
     * @returns {void}
     */
    _onMouseUp() {
        this._handlePointerUp();
    }

    /**
     * Handles touch end event to end dragging or resizing.
     *
     * @private
     * @returns {void}
     */
    _onTouchEnd() {
        this._handlePointerUp();
    }

    /**
     * Handles pointer up for both mouse and touch.
     *
     * @private
     * @returns {void}
     */
    _handlePointerUp() {
        const { isDragging, isResizing, position, size } = this.state;

        if (isDragging) {
            this.setState({ isDragging: false });
            this.props.onDragEnd && this.props.onDragEnd(position);
        } else if (isResizing) {
            this.setState({ isResizing: false });
            this.props.onResizeEnd && this.props.onResizeEnd(size);
        }
    }

    /**
     * Constrains position to viewport boundaries (bottom-right based).
     *
     * @param {Object} position - The position to constrain (distance from bottom-right).
     * @private
     * @returns {Object} The constrained position.
     */
    _constrainToViewport(position: { x: number, y: number }) {
        const { size } = this.state;
        const margin = 10; // Minimum margin from viewport edges

        // For bottom-right positioning:
        // - position.x is distance from right edge
        // - position.y is distance from bottom edge
        // - We need to ensure the component stays within viewport bounds

        const maxRightDistance = getWindowWidth() - size.width - margin;
        const maxBottomDistance = window.innerHeight - size.height - margin;

        return {
            x: Math.max(margin, Math.min(position.x, maxRightDistance)),
            y: Math.max(margin, Math.min(position.y, maxBottomDistance))
        };
    }

    /**
     * Constrains size to min/max boundaries.
     *
     * @param {Object} size - The size to constrain.
     * @private
     * @returns {Object} The constrained size.
     */
    _constrainSize(size: { width: number, height: number }) {
        const { minSize, maxSize } = this.props;

        return {
            width: Math.max(
                minSize?.width || 150,
                Math.min(size.width, maxSize?.width || 600)
            ),
            height: Math.max(
                minSize?.height || 80,
                Math.min(size.height, maxSize?.height || 400)
            )
        };
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { children, className, draggable } = this.props;
        const { position, size, isDragging, isResizing } = this.state;

        const containerStyle = {
            position: 'absolute',
            right: position.x,
            bottom: position.y,
            width: size.width,
            borderRadius: '20px',
            height: size.height,
            cursor: isDragging ? 'grabbing' : draggable ? 'move' : 'default',
            transition: isDragging || isResizing ? 'none' : 'all 0.2s ease-in-out',
            zIndex: isDragging || isResizing ? 1001 : 1000,

            // Improve touch handling
            touchAction: 'none',
            userSelect: 'none'
        };

        const containerClass = `draggable-resizable ${className || ''} ${
            isDragging ? 'dragging' : ''
        } ${isResizing ? 'resizing' : ''}`.trim();

        // const handleResizeStyle = {
        //     position: 'absolute',
        //     top: 0,
        //     left: 0,
        //     width: 20,
        //     height: 20,
        //     cursor: 'nw-resize',
        //     background: 'transparent',

        //     // Make resize handle more touch-friendly
        //     minWidth: '20px',
        //     minHeight: '20px'
        // };

        return (
            <div
                className = { containerClass }
                onMouseDown = { this._onMouseDown }
                onTouchStart = { this._onTouchStart }
                style = { containerStyle }>
                {children}
                {/* {resizable && (
                    <div
                        className = 'resize-handle'
                        onMouseDown = { this._onResizeMouseDown }
                        onTouchStart = { this._onResizeTouchStart }
                        style = { handleResizeStyle } />
                )} */}
            </div>
        );
    }
}
